import {
	BCAlert,
	BCButton,
	BCButtonOverflow,
	BCCard,
	BCCombobox,
	BCNotification,
	BCSkeleton,
	BCViewer,
	findMessage,
	IAlertTypes,
	IButtonOverflowItem,
	IComboboxSelectedChangeEventArgs
} from '@bimser/components';
import { IItem, isNullOrUndefined, selectFile } from '@bimser/core';
import IconClose from '@bimser/icons/16/close-panel';
import IconPrint from '@bimser/icons/16/print';
import IconRefresh from '@bimser/icons/16/refresh2';
import IconUpload from '@bimser/icons/16/upload';
import classNames from 'classnames/bind';
import * as React from 'react';
import { useReactToPrint } from 'react-to-print';
import { MessageBox } from '../../..';
import { DropZone } from '../../../utilities/helpers';
import { useMountedState } from '../../../utilities/hooks';
import Style from '../assets/style.scss';
import { isViewerDropType, ViewerType } from '../entities';
import IDmViewerProps from '../entities/IDmViewerProps';
import ChatWithDocument from './ChatWithDocument';

const cx = classNames.bind(Style);
const overflowButtonProps = { type: 'text' as const };
const DmViewer = (props: IDmViewerProps) => {
	const isMounted = useMountedState();
	const printRef = React.useRef<any>();

	const [editData, setEditData] = React.useState<any>(null);

	React.useEffect(() => {
		if (props.item?.viewerType === ViewerType.GroupDocsViewer) {
			setEditData(null);
		}
	}, [props.item?.data?.url]);

	React.useEffect(() => {
		if (isMounted) {
			setEditData(null);
		}
		props.onCloseAlert(null);
		if (props.item && props.onHideActions) {
			props.onHideActions(props.item.viewerType === ViewerType.NotificationViewer);
		}

		return () => {
			if (editData != null) {
				URL.revokeObjectURL(editData);
			}
		};
	}, [props.item && props.item.id]);

	function onChangeData(value: string) {
		props.onChangeData({ id: props.item.id, data: value });
	}

	function getViewerData() {
		if (
			props.item &&
			isViewerDropType(props.item.viewerType) &&
			props.item.mode == 'edit' &&
			editData !== null
		) {
			return editData;
		}
		return props.item.data;
	}

	function isDropAllowed() {
		if (props.item && props.item.mode == 'edit' && isViewerDropType(props.item.viewerType)) {
			return true;
		}
		return false;
	}

	function onFileDrop(files: File[], ev: React.DragEvent) {
		if (files && files.length) {
			setEditFile(files[0]);
		}
	}

	function setEditFile(file: File) {
		if (file.type !== props.item?.mimeType) {
			BCNotification.error({
				message: findMessage.get('103677')
			});
			return;
		}

		if (editData != null) {
			URL.revokeObjectURL(editData);
		}

		new Promise(r => {
			if (file.name != props.item.fileName) {
				MessageBox.confirm({
					content: findMessage.get('103235')
				}).then(result => {
					if (result.button.id == 'OK') {
						r(true);
					}
				});
			} else {
				r(true);
			}
		}).then(() => {
			setEditData(
				props.item.viewerType == ViewerType.PDFViewer ||
					props.item.viewerType == ViewerType.GroupDocsViewer
					? file
					: URL.createObjectURL(file)
			);
			props.onChangeFileSet({ id: props.item.id, data: file });
			props.onChangeFile();
		});
	}

	function onBrowseClick() {
		selectFile(props.item?.mimeType || '*', (ev: Event) => {
			const files = (ev.target as HTMLInputElement).files;
			for (const file of Array.from(files)) {
				setEditFile(file);
			}
		});
	}

	function getRibbonFarItems() {
		const items: IButtonOverflowItem[] = [];

		if (
			props.item &&
			isViewerDropType(props.item.viewerType || props.item.userSelectedViewerType) &&
			props.item.mode == 'edit'
		) {
			items.push({
				key: 'browse',
				buttonProps: {
					children: findMessage.get('100719'),
					icon: <IconUpload />,
					onClick: onBrowseClick,
					type: 'text'
				}
			});
		}

		return items;
	}

	const getViewerTypeText = (type: ViewerType) => {
		switch (type) {
			case ViewerType.TextViewer:
				return findMessage.get('100204');
			case ViewerType.VideoViewer:
				return 'Video';
			case ViewerType.ImageViewer:
				return findMessage.get('1010044');
			case ViewerType.PDFViewer:
				return 'PDF';
			case ViewerType.TiffViewer:
				return 'TIFF';
			case ViewerType.GroupDocsViewer:
				return 'GroupDocs';
			case ViewerType.OfficeViewer:
				return 'Office';
		}
	};

	const viewerOptions = React.useMemo(
		() =>
			props.selectableViewerTypes.map(
				type =>
					({
						key: type.toString(),
						value: type,
						text: getViewerTypeText(type)
					}) as IItem
			),
		[props.selectableViewerTypes]
	);

	const onChangeUserSelectedViewerType = (args: IComboboxSelectedChangeEventArgs) => {
		if (+args.data != props.item.userSelectedViewerType) {
			props.onChangeUserSelectedViewerType({
				viewerId: props.item.id,
				userSelectedViewerType: args.data ? +args.data : null
			});
		}
	};

	const isRibbonDisabled = () => props.disableRibbon || isNullOrUndefined(props.ribbonBarItems);

	const handlePrint = useReactToPrint({
		content: () => printRef.current
	});

	const ribbonProps = (): IButtonOverflowItem[] => {
		let _ribbonBarItems = props.ribbonBarItems ? [...props.ribbonBarItems] : [];
		let viewerType = props.item?.viewerType;

		if (
			(isNullOrUndefined(viewerType) || viewerType == ViewerType.Unsupported) &&
			props.item?.userSelectedViewerType
		) {
			viewerType = props.item.userSelectedViewerType;
		}

		if (
			[
				ViewerType.ImageViewer,
				ViewerType.TiffViewer,
				ViewerType.PDFViewer,
				ViewerType.TextViewer
			].includes(viewerType) &&
			props.canPrint
		) {
			const printButtonIndex = _ribbonBarItems.findIndex(item => item.key == 'print');
			const printRibbonItem: IButtonOverflowItem = {
				key: 'print',
				buttonProps: {
					children: findMessage.get('101166'),
					icon: <IconPrint />,
					onClick: () => handlePrint(),
					type: 'text',
					disabled: props.loading
				}
			};

			if (printButtonIndex > -1) {
				_ribbonBarItems[printButtonIndex] = printRibbonItem;
			} else {
				_ribbonBarItems.unshift(printRibbonItem);
			}
		}

		return _ribbonBarItems;
	};

	const handleCloseAlert = () => {
		props.onCloseAlert(null);
	};

	const handleUpdateFile = () => {
		props.onViewerOfficeRefresh({
			data: props.item.id,
			panelId: props.item.panelId
		});
		handleCloseAlert();
	};

	const chatWithDocumentRender = () => {
		return props.chatWithDocumentSession ? (
			<BCCard
				className={Style.chatCard}
				extra={
					<BCButton
						type="text"
						onClick={props.handleCloseChat}
						className={Style.closeBtn}
					>
						<IconClose />
					</BCButton>
				}
				title={findMessage.get('103588')}
			>
				<ChatWithDocument sessionID={props.chatWithDocumentSession} />
			</BCCard>
		) : (
			''
		);
	};

	function renderViewerHeader() {
		const _ribbonPropsItems = ribbonProps();

		if (isRibbonDisabled() || !_ribbonPropsItems.length) {
			return <></>;
		}

		return (
			<div
				key="viewerHeader"
				className={Style.viewerHeader}
			>
				<BCButtonOverflow
					items={[...getRibbonFarItems(), ..._ribbonPropsItems]}
					overflowButtonProps={overflowButtonProps}
					alignRight
				/>
			</div>
		);
	}

	return (
		<div
			className={
				cx({
					DmViewerContainer: true,
					disabledHeader: isRibbonDisabled()
				}) +
				' ' +
				(props.className ? props.className : '')
			}
		>
			{renderViewerHeader()}
			<div
				className={cx({
					viewerContainer: true,
					isAIActive: props.chatWithDocumentSession
				})}
			>
				<BCSkeleton
					className={Style.viewerSkeleton}
					contentType={props.skeletonType}
					loadingStatus={isNullOrUndefined(props.item) || props.loading}
				>
					<DropZone
						allowDrag={isDropAllowed()}
						allowDrop={isDropAllowed()}
						onFiles={onFileDrop}
					>
						{_props => (
							<div
								className={cx({ viewerContent: true, dragOver: _props.over })}
								style={{ padding: 5 }}
								{..._props.events}
							>
								{props.item.viewerType == ViewerType.Unsupported && (
									<BCAlert
										message={
											<div className={Style.viewerTypeSelection}>
												<span>{findMessage.get('102755')}</span>
												<BCCombobox
													className={Style.viewerTypeCombobox}
													showSearch={false}
													allowClear
													defaultValue={props.item.userSelectedViewerType}
													options={viewerOptions}
													onSelectedChange={
														onChangeUserSelectedViewerType
													}
												/>
											</div>
										}
										type={IAlertTypes.info}
										showIcon
									/>
								)}
								{props.viewersAlertMessage && (
									<BCAlert
										message={
											<div className={Style.viewerTypeOffice}>
												<span>{props.viewersAlertMessage.message}</span>
												<BCButton
													onClick={handleUpdateFile}
													className={Style.viewerButton}
													icon={
														props.viewersAlertMessage.refreshButton && (
															<IconRefresh
																className={
																	Style.viewerRefreshButton
																}
															/>
														)
													}
												/>
											</div>
										}
										type={props.viewersAlertMessage.type}
										showIcon
										closeIcon={
											<IconClose
												className={Style.viewerCloseButton}
												onClick={handleCloseAlert}
											/>
										}
									/>
								)}
								<div
									className={cx({
										bcViewerWrapper: true,
										hasViewerTypeSelection:
											props.item.viewerType == ViewerType.Unsupported
									})}
								>
									<BCViewer
										printRef={printRef}
										key={props.item.id}
										mode={props.item.mode}
										readOnly={props.item.mode != 'edit'}
										type={
											props.item.viewerType == ViewerType.Unsupported &&
											props.item.userSelectedViewerType
												? props.item.userSelectedViewerType
												: props.item.viewerType
										}
										mimeType={props.item.mimeType}
										language={props.item.language}
										data={getViewerData()}
										onChange={onChangeData}
										panelSize={props.panelSize}
										noContentTitle={findMessage.get('101392')}
									/>
								</div>
							</div>
						)}
					</DropZone>
				</BCSkeleton>
				{chatWithDocumentRender()}
			</div>
		</div>
	);
};

export default DmViewer;
